/**
 * PlayCanvas Player Component
 *
 * ARCHITECTURE:
 * PlayCanvas engine is a **singleton** — it is loaded once and never destroyed.
 * Import maps can only be registered once per page (per spec), so re-loading
 * the engine after destroy() causes "listBundlesForAsset is null" because the
 * new importmap is silently ignored and the old (destroyed) module is reused.
 *
 * On React unmount the <canvas> is "parked" in a hidden DOM element so the
 * WebGL context stays alive. On re-mount the canvas is adopted back into the
 * visible container and a resize is triggered so PlayCanvas reflows.
 *
 * Route changes switch boatType via window.configurator.setConfig({ boatType }).
 */

import React, { useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import s from './PlayCanvasPlayer.module.scss';
import {
  PlayCanvasConfigurator,
  ROUTE_TO_BOAT_TYPE,
  syncStoreFromPlayCanvasConfig,
} from '../../utils/playcanvasConfig';
import { initPlayCanvasSettings, setActiveItem } from '../../redux/actions/Settings';

// ─── Configuration ───────────────────────────────────────────────
const PLAYCANVAS_CONFIG = {
  baseUrl: 'https://2d-render-admin-storage.fra1.cdn.digitaloceanspaces.com',
  idProject: '411',
  idProduct: '2643',
} as const;

// ─── Types ───────────────────────────────────────────────────────
interface PlayCanvasWindow extends Window {
  pc?: any;
  pcBootstrap?: any;
  configurator?: PlayCanvasConfigurator & {
    setConfiguration?: (config: Record<string, any>) => void;
  };
}

// ─── URL helpers ─────────────────────────────────────────────────
const generateUrls = (
  baseUrl: string,
  idProject: string,
  idProduct: string
) => {
  const base = `${baseUrl}/projects/${idProject}/products/${idProduct}/playcanvas/`;
  return {
    base,
    indexScript: `${base}js/index.mjs`,
    styles: `${base}styles.css`,
    manifest: `${base}manifest.json`,
  };
};

// ─── DOM helpers ─────────────────────────────────────────────────
const addLink = (href: string, rel: string, type?: string): void => {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (type) link.type = type;
  document.head.appendChild(link);
};

const addImportMap = (baseUrl: string, cacheBust: string) => {
  // Only add once — browsers ignore subsequent importmaps after any module loads
  if (document.querySelector('script[type="importmap"]')) return;
  const script = document.createElement('script');
  script.type = 'importmap';
  script.textContent = JSON.stringify({
    imports: {
      playcanvas: `${baseUrl}js/playcanvas-stable.min.mjs?v=${cacheBust}`,
    },
  });
  document.head.appendChild(script);
};

const loadModuleScript = (src: string, cacheBust: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = `${src}?v=${cacheBust}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load: ${src}`));
    document.body.appendChild(script);
  });
};

const waitForPlayCanvas = async (timeoutMs = 15000): Promise<boolean> => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const win = window as unknown as PlayCanvasWindow;
    if (win.pc && document.getElementById('application-canvas')) return true;
    await new Promise((r) => setTimeout(r, 100));
  }
  throw new Error('PlayCanvas initialization timeout');
};

// ─── Singleton engine manager ────────────────────────────────────
// These variables live at module scope — they survive React mount/unmount.
let _engineReady = false;
let _engineLoading: Promise<void> | null = null;
let _canvas: HTMLCanvasElement | null = null;
let _hiddenPark: HTMLDivElement | null = null;

/** Hidden off-screen container that holds the canvas between React mounts. */
function getHiddenPark(): HTMLDivElement {
  if (!_hiddenPark) {
    _hiddenPark = document.createElement('div');
    _hiddenPark.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;';
    document.body.appendChild(_hiddenPark);
  }
  return _hiddenPark;
}

/** Get (or create) the persistent canvas element. */
function getCanvas(): HTMLCanvasElement {
  if (_canvas) return _canvas;
  _canvas = document.createElement('canvas');
  _canvas.id = 'application-canvas';
  // Park it immediately so PlayCanvas can find it by ID during init
  getHiddenPark().appendChild(_canvas);
  return _canvas;
}

/** Load the engine exactly once. Subsequent calls return the cached promise. */
async function ensureEngineLoaded(): Promise<void> {
  if (_engineReady) return;
  if (_engineLoading) return _engineLoading;

  _engineLoading = (async () => {
    const cacheBust = String(Date.now());
    const urls = generateUrls(
      PLAYCANVAS_CONFIG.baseUrl,
      PLAYCANVAS_CONFIG.idProject,
      PLAYCANVAS_CONFIG.idProduct
    );

    // Make sure the canvas exists in the DOM before PlayCanvas looks for it
    getCanvas();

    addLink(urls.styles, 'stylesheet', 'text/css');
    addLink(urls.manifest, 'manifest');
    addImportMap(urls.base, cacheBust);

    // Small delay so the importmap is picked up before module resolution
    await new Promise((r) => setTimeout(r, 50));

    await loadModuleScript(urls.indexScript, cacheBust);
    await waitForPlayCanvas(15000);

    _engineReady = true;
  })();

  return _engineLoading;
}

/** Wait until window.configurator appears (polled). */
function waitForConfigurator(timeoutMs = 10000): Promise<PlayCanvasConfigurator> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const id = setInterval(() => {
      const win = window as unknown as PlayCanvasWindow;
      if (win.configurator) {
        clearInterval(id);
        resolve(win.configurator);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(id);
        reject(new Error('configurator API timeout'));
      }
    }, 200);
  });
}

// ─── React Component ─────────────────────────────────────────────
export const PlayCanvasPlayer: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    _engineReady ? 'ready' : 'idle'
  );
  const [error, setError] = useState<string | null>(null);

  const { id } = useParams<{ id: string }>();
  const boatType = ROUTE_TO_BOAT_TYPE[id] ?? 'intrigue';
  const dispatch = useDispatch();

  // Keep a stable ref so async callbacks always have the latest dispatch
  const dispatchRef = useRef(dispatch);
  useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

  /** Sync Redux store from the current window.configurator.getConfig() snapshot. */
  function syncActive() {
    syncStoreFromPlayCanvasConfig((attrName, data) =>
      dispatchRef.current(setActiveItem({ name: attrName, data }))
    );
  }

  // ── Adopt / park the persistent canvas ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = getCanvas();

    // Move canvas from hidden park into the visible container
    container.appendChild(canvas);
    canvas.className = s.canvas;

    // Trigger resize so PlayCanvas reflows to the new container size
    const triggerResize = () => {
      const win = window as unknown as PlayCanvasWindow;
      const pcApp =
        win.pc?.Application?.getApplication?.() ?? win.pcBootstrap?.app;
      if (pcApp?.resizeCanvas) {
        pcApp.resizeCanvas();
      }
      window.dispatchEvent(new Event('resize'));
    };

    // Slight delay to let layout settle after DOM append
    const rafId = requestAnimationFrame(triggerResize);

    return () => {
      cancelAnimationFrame(rafId);
      // Park canvas off-screen — do NOT remove from DOM or destroy
      getHiddenPark().appendChild(canvas);
    };
  }, []);

  // ── One-time engine initialisation ──
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (_engineReady) {
        setStatus('ready');
        // If engine already loaded, still need to init settings for this boat
        try {
          const configurator = await waitForConfigurator(5000);
          if (cancelled) return;
          await configurator.setConfig({ boatType });
          const config = configurator.getConfig();
          const availableOptions = configurator.getAvailableOptions();
          dispatch(initPlayCanvasSettings({ config, availableOptions, boatType }));

          // Also sync active items
          syncActive();
        } catch (err) {
          console.warn('[PlayCanvas] re-init settings failed:', err);
        }
        return;
      }

      setStatus('loading');
      try {
        await ensureEngineLoaded();
        if (cancelled) return;
        setStatus('ready');

        // Push initial boatType once configurator appears
        const configurator = await waitForConfigurator();
        if (cancelled) return;

        // Set the boat type based on the route
        await configurator.setConfig({ boatType });

        // Read config + available options and dispatch to Redux
        const config = configurator.getConfig();
        const availableOptions = configurator.getAvailableOptions();
        dispatch(initPlayCanvasSettings({ config, availableOptions, boatType }));

        // Sync active items from initial config
        syncActive();

        // Subscribe to PlayCanvas state changes to keep Redux in sync
        configurator.subscribe((newState, oldState) => {
          if (cancelled) return;

          if (newState.boatType !== oldState.boatType) {
            // Boat type changed — full rebuild of settings
            const newOptions = configurator.getAvailableOptions();
            dispatchRef.current(initPlayCanvasSettings({
              config: newState,
              availableOptions: newOptions,
              boatType: newState.boatType,
            }));
          }

          // Lightweight sync for every config change (material selections)
          syncActive();
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        console.error('PlayCanvas init failed:', msg, err);
        setStatus('error');
        setError(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── React to route changes — switch boatType on the live engine ──
  useEffect(() => {
    if (status !== 'ready') return;
    const win = window as unknown as PlayCanvasWindow;
    if (win.configurator?.setConfig) {
      console.log('[PlayCanvas] route → setConfig boatType:', boatType);
      (async () => {
        await win.configurator!.setConfig({ boatType });
        const config = win.configurator!.getConfig();
        const availableOptions = win.configurator!.getAvailableOptions();
        dispatch(initPlayCanvasSettings({ config, availableOptions, boatType }));
      })();
    }
  }, [boatType, status, dispatch]);

  function resetView() {
    (window as any).api?.camera?.resetToDefault?.();
  }

  return (
    <div ref={containerRef} className={s.container} data-status={status}>
      {/* Canvas is managed programmatically — not rendered in JSX */}

      {status === 'ready' && (
        <button
          className={s.resetBtn}
          onClick={resetView}
          title="Reset camera to default view"
          aria-label="Reset view"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6a6 6 0 0 1-6 6 6 6 0 0 1-6-6H4a8 8 0 0 0 8 8 8 8 0 0 0 8-8 8 8 0 0 0-8-8Z"
              fill="currentColor"
            />
          </svg>
          <span>Reset View</span>
        </button>
      )}

      {status === 'loading' && (
        <div className={s.overlay}>
          <div className={s.loading}>
            <div className={s.title}>Loading 3D Configurator...</div>
            <div className={s.subtitle}>Please wait</div>
            <div className={s.spinner} />
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className={s.overlay}>
          <div className={s.error}>
            <div className={s.error_title}>Error Loading Configurator</div>
            <div className={s.error_message}>{error}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayCanvasPlayer;
